import React from 'react';
import { Panel, type DateRange } from '../../../ui-kit';
import SpecTableBlock, { specTableFooter } from './SpecTableBlock';
import TableSectionActions, { TableSectionTools } from './TableSectionActions';
import { useTableSection } from '../useTableSection';
import type { ManualDashboardPayload, SpecTableBlockDef } from '../../../types/manualDashboard';

interface SpecTablePanelProps {
  dashboardId?: string;
  /** The table as the board payload has it, at the board's grouping. */
  block: SpecTableBlockDef;
  payload: ManualDashboardPayload;
  range: DateRange;
  filters: Record<string, string[]>;
  refreshNonce: number;
  loading: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Start with every section folded, showing only their titles. */
  groupsCollapsed?: boolean;
  loadingHeight?: number;
  onGrainChange?: (blockId: string, grain: string | undefined) => void;
}

/**
 * One of the board's metric tables, in its panel, with its own cohort
 * grouping and export.
 */
export default function SpecTablePanel({
  dashboardId,
  block,
  payload,
  range,
  filters,
  refreshNonce,
  loading,
  collapsible = false,
  defaultCollapsed = false,
  groupsCollapsed = false,
  loadingHeight = 320,
  onGrainChange,
}: SpecTablePanelProps) {
  const section = useTableSection({
    dashboardId, blockId: block.id, payload, range, filters, refreshNonce, onGrainChange,
  });
  // Kept here rather than in the table, which is swapped for a placeholder
  // while it reloads; sections the reader opened stay open across a regroup.
  const [collapsedGroups, setCollapsedGroups] = React.useState<string[]>(() =>
    groupsCollapsed ? block.sections.map((_, i) => String(i)) : [],
  );

  const view =
    (section.source.blocks.find((b) => b.type === 'spec_table' && b.id === block.id) as
      | SpecTableBlockDef
      | undefined) ?? block;

  // Drawn twice when expanded — in the panel and full screen — so built once.
  const breakdownMetrics = React.useMemo(
    () => payload.metrics.filter((m) => m.agent_breakdown).map((m) => m.key),
    [payload.metrics],
  );
  const agentBreakdown =
    dashboardId && breakdownMetrics.length ? { dashboardId, metrics: breakdownMetrics, filters } : undefined;

  const table = (fill: boolean) => (
    <SpecTableBlock
      sections={view.sections}
      periods={section.source.periods}
      showSparkline={view.sparkline_column}
      title={view.title ?? undefined}
      collapsedGroups={collapsedGroups}
      onCollapsedGroupsChange={setCollapsedGroups}
      fill={fill}
      agentBreakdown={agentBreakdown}
    />
  );

  return (
    <Panel
      title={block.title}
      subtitle={block.subtitle}
      tools={<TableSectionTools section={section} />}
      actions={<TableSectionActions section={section} disabled={loading} />}
      loading={loading || section.regrouping}
      loadingHeight={loadingHeight}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
      flush
      footer={section.problem ?? specTableFooter(view.sections)}
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
